import { Drawer, Grid2, FormControlLabel, Checkbox } from "@mui/material";
import { useState, useEffect } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { DataTypeEnum } from "../../../_common/enum/dataType.enum";
import { IClientContactManage } from "../../../_common/models/clientContact/clientContactManage.model";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import { StandardButton } from "../../../_components/button/primary.button";
import { DrawerHeader } from "../../../_components/drawer/header.drawer";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { centreContactService } from "../../../_services/centreContact.service";
import { clientContactService } from "../../../_services/clientContact.service";
import { clientContactTypesService } from "../../../_services/clientContactTypes.service";


export const ClientContactManageDrawer = (params: { isOpen?: boolean, clientId: string, onSave(): void, onClose(): void }) => {
    const { id } = useParams<{ id: string }>();

    const resetData = (): IClientContactManage => ({
        id: id ? Number(id) : 0,
        clientContactTypeId: '',
        fName: '',
        lName: '',
        address1: '',
        address2: '',
        address3: '',
        town: '',
        countyId: null,
        phone: '',
        fax: '',
        email: '',
        clientId: Number(params.clientId),
        mobilePhone: '',
        notify: false,
        encrypt: false,
        isActive: true,
        clientContactTypeEmailRequired: false
    });

    const [data, setData] = useState<IClientContactManage>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    useEffect(() => {
        if (isOpen == false)
            setIsOpen(true);
    }, []);

    useEffect(() => {
        fetchEmailRequired();
    }, [data.clientContactTypeId])

    function fetchEmailRequired() {
        if (!data.clientContactTypeId) return;

        clientContactTypesService
            .get(data.clientContactTypeId)
            .then(item => {
                setData(x => ({
                    ...x,
                    clientContactTypeEmailRequired: item.emailRequired
                }));
            });
    }

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return clientContactService
            .get(id)
            .then(
                (json: IClientContactManage) => {
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);
        return clientContactService
            .submit(data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        setIsLoading(true);
        return clientContactService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    function handleCheckboxChange(event: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: event.target.checked } });
    };

    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={isOpen}
            ModalProps={{
                keepMounted: true
            }}>

            <DrawerHeader name={(!!id) ? "Update Client Contact" : "Create Client Contact"} onClose={params.onClose} />

            <ValidatorForm
                onSubmit={submit}
                noValidate>

                <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            name="clientId"
                            label="Client"
                            required
                            disabled={!!params.clientId}
                            value={data.clientId}
                            onChange={handleChange} dataType={DataTypeEnum.ClientNames} />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            name="clientContactTypeId"
                            label="Contact Type"
                            required
                            value={data.clientContactTypeId}
                            onChange={handleChange} dataType={DataTypeEnum.ClientContactTypes} />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="fName"
                            name="fName"
                            label="First Name"
                            fullWidth
                            required
                            validators={['required', 'minStringLength:1']}
                            errorMessages={['required field', "Field cannot be empty"]}
                            value={data.fName}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="lName"
                            name="lName"
                            label="Last Name"
                            fullWidth
                            required
                            validators={['required', 'minStringLength:1']}
                            errorMessages={['required field', "Field cannot be empty"]}
                            value={data.lName}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="address1"
                            name="address1"
                            label="Address 1"
                            fullWidth
                            required
                            validators={['required', 'minStringLength:1']}
                            errorMessages={['required field', "Field cannot be empty"]}
                            value={data.address1}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="address2"
                            name="address2"
                            label="Address 2"
                            fullWidth
                            value={data.address2}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="address3"
                            name="address3"
                            label="Address 3"
                            fullWidth
                            value={data.address3}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="town"
                            name="town"
                            label="town"
                            fullWidth
                            value={data.town}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            name="countyId"
                            label="County"
                            value={data.countyId}
                            onChange={handleChange} dataType={DataTypeEnum.Counties} />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="phone"
                            name="phone"
                            label="Phone Number"
                            fullWidth
                            value={data.phone}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="mobilePhone"
                            name="mobilePhone"
                            label="Mobile Number"
                            fullWidth
                            value={data.mobilePhone}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            key="fax"
                            name="fax"
                            label="Fax"
                            fullWidth
                            value={data.fax}
                            onChange={handleChange}
                        />
                    </Grid2>

                    {data.clientContactTypeEmailRequired ?
                        <Grid2 size={{ xs: 6 }}>
                            <StandardTextValidator
                                key="email"
                                name="email"
                                label="Email"
                                required
                                fullWidth
                                validators={['required', 'minStringLength:1']}
                                errorMessages={['required field', "Field cannot be empty"]}
                                value={data.email}
                                onChange={handleChange}
                            />
                        </Grid2>
                        :
                        <Grid2 size={{ xs: 6 }}>
                            <StandardTextValidator
                                key="email"
                                name="email"
                                label="Email"
                                fullWidth
                                value={data.email}
                                onChange={handleChange}
                            />
                        </Grid2>
                    }
                    {data.clientContactTypeEmailRequired && (
                        <>
                            <Grid2 size={{ xs: 6 }}>
                                <FormControlLabel checked={!!data.notify} name="notify"
                                    control={<Checkbox onChange={handleCheckboxChange} color={"primary"} />}
                                    label="Notify By Email"
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 6 }}>
                                <FormControlLabel checked={!!data.encrypt} name="encrypt"
                                    control={<Checkbox onChange={handleCheckboxChange} color={"primary"} />}
                                    label="Encrypt Email"
                                />
                            </Grid2>
                        </>
                    )}
                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: !data.id ? 12 : data?.isActive === false ? 12 : 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="save"
                                    type="submit">
                                    {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                </StandardButton>
                            </Grid2>

                            {(!!id && data?.isActive === true) &&
                                <Grid2 size={{ xs: 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="block"
                                        variant="error"
                                        onClick={disable}>
                                        Disable
                                    </StandardButton>
                                </Grid2>
                            }
                        </Grid2>
                    </Grid2>
                </Grid2>

            </ValidatorForm>
        </Drawer>
    );
};